/**
 * Keeps a signed-in parent signed in when a stale OAuth callback replays.
 *
 * The sequence this guards against (YAR-438 QA, "OAuth state not found or
 * expired" on the landing page after pressing Back from the Founder's
 * letter): the browser's Back goes to Google's OAuth page — a history entry
 * we do not own — Google re-completes the flow with the `state` it already
 * used, Supabase Auth rejects that consumed state and bounces to the app
 * with `?error=…&error_description=…`. auth-js then classifies ANY url that
 * carries `error_description` as a failed implicit-grant callback and calls
 * `_removeSession()` — so a replayed, harmless error signs the parent out of
 * a session that had already succeeded. The app cannot remove Google's
 * history entry; what it can do is refuse to let a replayed error touch a
 * session that already exists.
 *
 * Runs BEFORE the Supabase client is constructed (see lib/supabase.ts): the
 * client reads `window.location` when it initialises, so the params have to
 * be gone by then. A url with error params and NO stored session is a
 * genuine failed sign-in and is left untouched — AuthContext still shows
 * that reason on the landing page.
 *
 * That moment is also before main.tsx has called `initSentry()`, where a
 * breadcrumb is a no-op, so what was dropped is parked here and handed to
 * the first reporter that runs after Sentry is up (`takeStaleOAuthCallback`,
 * called from AuthContext's init).
 */

const OAUTH_ERROR_KEYS = ['error', 'error_code', 'error_description'] as const;

export interface StaleOAuthCallback {
  error: string;
  description: string | null;
}

/**
 * The localStorage key auth-js keeps the session under. Mirrors supabase-js's
 * default (`sb-<project ref>-auth-token`, the ref being the first host
 * label) rather than importing it — supabase-js does not export the helper.
 */
export function authSessionStorageKey(supabaseUrl: string): string {
  return `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
}

/** Removes the OAuth error keys from one `key=value&…` string; null if it had none. */
function withoutOAuthError(
  params: URLSearchParams,
): { params: URLSearchParams; dropped: StaleOAuthCallback } | null {
  const error = params.get('error');
  const description = params.get('error_description');
  if (error === null && description === null) return null;
  const next = new URLSearchParams(params);
  for (const key of OAUTH_ERROR_KEYS) next.delete(key);
  return {
    params: next,
    dropped: { error: error ?? 'unknown', description },
  };
}

/**
 * Pure core. Given the page url and whether a session is already persisted,
 * returns the url to replace it with and what was dropped — or null when the
 * url should be left alone (no error params, or nothing to protect).
 * auth-js reads the error from the query (PKCE) and from the hash (implicit),
 * so both are cleaned.
 */
export function stripStaleOAuthError(
  href: string,
  hasStoredSession: boolean,
): { href: string; dropped: StaleOAuthCallback } | null {
  if (!hasStoredSession) return null;
  const url = new URL(href);
  const query = withoutOAuthError(url.searchParams);
  const hash = url.hash.startsWith('#')
    ? withoutOAuthError(new URLSearchParams(url.hash.slice(1)))
    : null;
  if (!query && !hash) return null;
  if (query) {
    const search = query.params.toString();
    url.search = search ? `?${search}` : '';
  }
  if (hash) {
    const rest = hash.params.toString();
    url.hash = rest ? `#${rest}` : '';
  }
  return { href: url.toString(), dropped: (query ?? hash)!.dropped };
}

/** What the boot-time guard dropped, until a reporter takes it. */
let parked: StaleOAuthCallback | null = null;

/**
 * Applies the guard to the live address bar. Returns what it dropped (and
 * parks it for `takeStaleOAuthCallback`); null when nothing changed. Storage
 * access is wrapped: a browser that blocks localStorage has no persisted
 * session to protect anyway.
 */
export function dropStaleOAuthErrorFromUrl(
  supabaseUrl: string,
): StaleOAuthCallback | null {
  let hasStoredSession = false;
  try {
    hasStoredSession =
      window.localStorage.getItem(authSessionStorageKey(supabaseUrl)) !== null;
  } catch {
    return null;
  }
  const result = stripStaleOAuthError(window.location.href, hasStoredSession);
  if (!result) return null;
  window.history.replaceState(window.history.state, '', result.href);
  parked = result.dropped;
  return result.dropped;
}

/**
 * Hands over what the guard dropped at boot, once — the caller is expected
 * to record it (a Sentry breadcrumb, from a point past `initSentry()`).
 * Null when the guard did nothing or the fact was already taken.
 */
export function takeStaleOAuthCallback(): StaleOAuthCallback | null {
  const dropped = parked;
  parked = null;
  return dropped;
}
